import { createContext, useContext, useMemo, useReducer } from 'react';

export const MODALS = {
  SHORTCUTS: 'shortcutsModal',
};

export const GlobalModalsContext = createContext({
  checkIsOpen: () => false,
  setIsOpen: () => {},
});

const INIT_STATE = Object.fromEntries(
  Object.values(MODALS).map((id) => [id, false]),
);

const modalStateReducer = (state, { type, id }) => {
  switch (type) {
    case 'OPEN_MODAL':
      return { ...state, [id]: true };
    case 'CLOSE_MODAL':
      return { ...state, [id]: false };
    default:
      throw new Error(`Unknown action type: ${type}`);
  }
};

/**
 * Manages open/close state for modals that can be opened anywhere in the app.
 */
export const GlobalModalsContextProvider = ({ children }) => {
  const [modalState, dispatchModalState] = useReducer(
    modalStateReducer,
    INIT_STATE,
  );

  const value = useMemo(
    () => ({
      checkIsOpen: (id) => modalState[id],
      setIsOpen: (id, val) => {
        if (val) {
          dispatchModalState({ type: 'OPEN_MODAL', id });
        } else {
          dispatchModalState({ type: 'CLOSE_MODAL', id });
        }
      },
    }),
    [modalState],
  );

  return (
    <GlobalModalsContext.Provider value={value}>
      {children}
    </GlobalModalsContext.Provider>
  );
};

export const useGlobalModal = (id) => {
  const { setIsOpen, checkIsOpen } = useContext(GlobalModalsContext);

  return {
    isOpen: checkIsOpen(id),
    setIsOpen: (isOpen) => setIsOpen(id, isOpen),
  };
};
